import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CalendarDays,
  CalendarPlus,
  ChevronDown,
  ChevronUp,
  MapPin,
  Pencil,
  Plus,
  RotateCcw,
  Search,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { buildRequesterHeaders } from "../utils/requester";
import ValueHelpSelect from "./ValueHelpSelect";

const handleCardKeyDown = (event, action) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

const getHolidayDateMeta = (dateStr) => {
  if (!dateStr) return { month: "CAL", day: "--", weekday: "", year: "" };
  try {
    const parts = String(dateStr).split("-");
    if (parts.length === 3) {
      const year = parseInt(parts[0], 10);
      const monthIndex = parseInt(parts[1], 10) - 1;
      const day = parseInt(parts[2], 10);
      const date = new Date(year, monthIndex, day);
      if (!isNaN(date.getTime())) {
        return {
          month: date.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
          day: String(day).padStart(2, "0"),
          weekday: date.toLocaleDateString("en-US", { weekday: "short" }),
          year: String(year),
        };
      }
    }
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
      return {
        month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
        day: String(d.getDate()).padStart(2, "0"),
        weekday: d.toLocaleDateString("en-US", { weekday: "short" }),
        year: String(d.getFullYear()),
      };
    }
  } catch {
    // fallback
  }
  return { month: "CAL", day: "--", weekday: "", year: "" };
};

const AdminHolidays = ({ user }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [form, setForm] = useState({
    name: "",
    date: "",
    type: "company",
    region: "",
    is_optional: false,
    description: "",
  });

  const load = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/holidays/`);
      setItems(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading holidays:", error);
      setMessage("Failed to load holidays");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((previous) => ({
      ...previous,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const reset = () => {
    setForm({
      name: "",
      date: "",
      type: "company",
      region: "",
      is_optional: false,
      description: "",
    });
    setEditing(null);
    setIsFormOpen(false);
  };

  const showToast = (nextMessage) => {
    setMessage(nextMessage);
    window.setTimeout(() => setMessage(""), 3000);
  };

  const save = async () => {
    if (!form.name || !form.date) {
      showToast("Name and date are required");
      return;
    }

    try {
      if (editing) {
        await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/api/holidays/update/${editing}`,
          form,
          { headers: buildRequesterHeaders(user) }
        );
        showToast("Holiday updated successfully");
      } else {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/holidays/add`, form, {
          headers: buildRequesterHeaders(user),
        });
        showToast("Holiday added successfully");
      }

      reset();
      load();
    } catch (error) {
      console.error("Error saving holiday:", error);
      showToast("Failed to save holiday");
    }
  };

  const editRow = (row) => {
    setEditing(row._id);
    setForm({
      name: row.name || "",
      date: row.date || "",
      type: row.type || "company",
      region: row.region || "",
      is_optional: !!row.is_optional,
      description: row.description || "",
    });
    setIsFormOpen(true);
    setTimeout(() => {
      document.getElementById("holiday-form-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;

    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/holidays/delete/${id}`, {
        headers: buildRequesterHeaders(user),
      });
      load();
      showToast("Holiday deleted successfully");
    } catch (error) {
      console.error("Error deleting holiday:", error);
      showToast("Failed to delete holiday");
    }
  };

  const summary = useMemo(() => {
    const optional = items.filter((item) => item.type === "optional" || item.is_optional).length;
    const publicCount = items.filter((item) => item.type === "public").length;
    const companyCount = items.filter((item) => item.type === "company").length;
    return { optional, publicCount, companyCount };
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const q = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !q ||
        (item.name || "").toLowerCase().includes(q) ||
        (item.description || "").toLowerCase().includes(q) ||
        (item.region || "").toLowerCase().includes(q) ||
        (item.date || "").includes(q);

      const matchesType =
        typeFilter === "all" ||
        (typeFilter === "optional" && (item.type === "optional" || item.is_optional)) ||
        (typeFilter === "company" && item.type === "company") ||
        (typeFilter === "public" && item.type === "public");

      return matchesSearch && matchesType;
    });
  }, [items, searchTerm, typeFilter]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="holiday-admin-workspace">
      <header className="admin-hero holiday-hero">
        <div className="holiday-hero-content">
          <div>
            <div className="admin-section-overline">Holiday Administration</div>
            <h1>Holiday Calendar</h1>
            <p className="holiday-hero-subtitle">Maintain enterprise closure days, regional observances, and optional holidays</p>
          </div>

          <div className="holiday-hero-action-row">
            <button
              type="button"
              className="fiori-button primary holiday-add-trigger-btn"
              onClick={() => {
                if (editing) reset();
                setIsFormOpen((prev) => !prev);
              }}
            >
              {isFormOpen ? <ChevronUp size={16} /> : <Plus size={16} />}
              <span>{isFormOpen ? "Close Editor" : "Add Holiday"}</span>
            </button>
          </div>
        </div>
      </header>

      <section className="holiday-summary-grid">
        <article
          className={`fiori-stat-card is-actionable ${typeFilter === "all" ? "is-selected" : ""}`}
          onClick={() => {
            setTypeFilter("all");
            scrollToSection("holiday-directory");
          }}
          onKeyDown={(event) => handleCardKeyDown(event, () => scrollToSection("holiday-directory"))}
          role="button"
          tabIndex={0}
        >
          <div className="fiori-stat-topline">
            <span className="fiori-stat-label">Total Holidays</span>
            <CalendarDays size={18} />
          </div>
          <div className="fiori-stat-value">{items.length}</div>
          <div className="fiori-stat-note">All scheduled holidays across the enterprise calendar</div>
        </article>

        <article
          className={`fiori-stat-card is-actionable ${typeFilter === "public" ? "is-selected" : ""}`}
          onClick={() => {
            setTypeFilter((prev) => (prev === "public" ? "all" : "public"));
            scrollToSection("holiday-directory");
          }}
          onKeyDown={(event) => handleCardKeyDown(event, () => scrollToSection("holiday-directory"))}
          role="button"
          tabIndex={0}
        >
          <div className="fiori-stat-topline">
            <span className="fiori-stat-label">Public</span>
            <CalendarDays size={18} />
          </div>
          <div className="fiori-stat-value">{summary.publicCount}</div>
          <div className="fiori-stat-note">Mandatory public holidays for enterprise</div>
        </article>

        <article
          className={`fiori-stat-card is-actionable ${typeFilter === "company" ? "is-selected" : ""}`}
          onClick={() => {
            setTypeFilter((prev) => (prev === "company" ? "all" : "company"));
            scrollToSection("holiday-directory");
          }}
          onKeyDown={(event) => handleCardKeyDown(event, () => scrollToSection("holiday-directory"))}
          role="button"
          tabIndex={0}
        >
          <div className="fiori-stat-topline">
            <span className="fiori-stat-label">Company</span>
            <Sparkles size={18} />
          </div>
          <div className="fiori-stat-value">{summary.companyCount}</div>
          <div className="fiori-stat-note">Company events and org-wide closure days</div>
        </article>

        <article
          className={`fiori-stat-card is-actionable ${typeFilter === "optional" ? "is-selected" : ""}`}
          onClick={() => {
            setTypeFilter((prev) => (prev === "optional" ? "all" : "optional"));
            scrollToSection("holiday-directory");
          }}
          onKeyDown={(event) => handleCardKeyDown(event, () => scrollToSection("holiday-directory"))}
          role="button"
          tabIndex={0}
        >
          <div className="fiori-stat-topline">
            <span className="fiori-stat-label">Optional</span>
            <Plus size={18} />
          </div>
          <div className="fiori-stat-value">{summary.optional}</div>
          <div className="fiori-stat-note">Floating & optional holidays to choose from</div>
        </article>
      </section>

      <section className="holiday-admin-grid">
        {/* Collapsible Form Panel */}
        <section className="fiori-panel holiday-form-panel" id="holiday-form-panel">
          <div
            className="fiori-panel-header holiday-panel-header-toggle"
            onClick={() => setIsFormOpen((prev) => !prev)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsFormOpen((prev) => !prev);
              }
            }}
          >
            <div>
              <div className="holiday-panel-header-title-row">
                <CalendarPlus size={20} className="holiday-panel-icon" />
                <h3>{editing ? "Edit Holiday" : "Add Holiday"}</h3>
                {editing && <span className="holiday-editing-pill">Editing mode</span>}
              </div>
              <p>Create or update a holiday entry used by the calendar</p>
            </div>
            <button
              type="button"
              className="fiori-button secondary compact holiday-toggle-btn"
              onClick={(e) => {
                e.stopPropagation();
                setIsFormOpen((prev) => !prev);
              }}
              title={isFormOpen ? "Hide holiday form" : "Show holiday form"}
            >
              {isFormOpen ? <ChevronUp size={16} /> : <Plus size={16} />}
              <span>{isFormOpen ? "Hide Form" : editing ? "Open Editor" : "New Holiday"}</span>
            </button>
          </div>

          <div className={`holiday-form-collapsible ${isFormOpen || editing ? "is-open" : "is-collapsed"}`}>
            <div className="holiday-form-grid">
              <label className="fiori-form-field">
                <span className="fiori-field-label">Holiday Name *</span>
                <input
                  className="input"
                  name="name"
                  placeholder="e.g. Diwali, Pongal, New Year"
                  value={form.name}
                  onChange={change}
                />
              </label>

              <label className="fiori-form-field">
                <span className="fiori-field-label">Date *</span>
                <input className="input" name="date" type="date" value={form.date} onChange={change} />
              </label>

              <div className="fiori-form-field">
                <span className="fiori-field-label">Type</span>
                <ValueHelpSelect
                  value={form.type}
                  onChange={(val) => setForm((prev) => ({ ...prev, type: val }))}
                  searchPlaceholder="Select holiday type"
                  options={[
                    { value: "company", label: "Company Holiday" },
                    { value: "public", label: "Public Holiday" },
                    { value: "optional", label: "Optional Holiday" },
                  ]}
                />
              </div>

              <label className="fiori-form-field">
                <span className="fiori-field-label">Region</span>
                <input
                  className="input"
                  name="region"
                  placeholder="Region or office location"
                  value={form.region}
                  onChange={change}
                />
              </label>

              <label className="fiori-form-field holiday-form-wide">
                <span className="fiori-field-label">Description</span>
                <input
                  className="input"
                  name="description"
                  placeholder="Additional context or notes for this holiday"
                  value={form.description}
                  onChange={change}
                />
              </label>

              <label className="holiday-toggle-row">
                <input
                  type="checkbox"
                  name="is_optional"
                  checked={form.is_optional}
                  onChange={change}
                />
                <span>Mark as optional holiday</span>
              </label>
            </div>

            <div className="admin-modal-actions holiday-form-actions">
              {editing && (
                <button type="button" className="fiori-button secondary" onClick={reset}>
                  Cancel edit
                </button>
              )}
              <button type="button" className="fiori-button primary" onClick={save}>
                {editing ? "Update holiday" : "Add holiday"}
              </button>
            </div>
          </div>
        </section>

        {/* Directory Panel */}
        <section className="fiori-panel holiday-directory-panel" id="holiday-directory">
          <div className="fiori-panel-header holiday-directory-header">
            <div>
              <h3>Holiday Directory</h3>
              <p>Review and maintain the current holiday schedule</p>
            </div>
            <div className="holiday-directory-count-badge">
              {filteredItems.length} {filteredItems.length === 1 ? "holiday" : "holidays"}
            </div>
          </div>

          {/* Quick Search & Filter Toolbar */}
          <div className="holiday-toolbar">
            <div className="holiday-search-wrap">
              <Search size={16} className="holiday-search-icon" />
              <input
                type="text"
                className="holiday-search-input"
                placeholder="Search holiday name, region, date..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="holiday-clear-search-btn"
                  onClick={() => setSearchTerm("")}
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <div className="holiday-filter-chips" role="tablist" aria-label="Filter holidays by type">
              {[
                { key: "all", label: `All (${items.length})` },
                { key: "company", label: `Company (${summary.companyCount})` },
                { key: "public", label: `Public (${summary.publicCount})` },
                { key: "optional", label: `Optional (${summary.optional})` },
              ].map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`holiday-filter-chip ${typeFilter === tab.key ? "is-active" : ""}`}
                  onClick={() => setTypeFilter(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="fiori-loading-card">
              <CalendarDays size={24} />
              <div>
                <strong>Loading holiday directory</strong>
                <p>Preparing existing holiday records.</p>
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="admin-empty-state">
              <CalendarDays size={28} />
              <div>
                <strong>No holidays found</strong>
                <p>
                  {searchTerm || typeFilter !== "all"
                    ? "Try adjusting your search query or filter selection."
                    : "No holidays have been added yet."}
                </p>
                {(searchTerm || typeFilter !== "all") && (
                  <button
                    type="button"
                    className="fiori-button secondary compact"
                    onClick={() => {
                      setSearchTerm("");
                      setTypeFilter("all");
                    }}
                    style={{ marginTop: 10 }}
                  >
                    <RotateCcw size={14} /> Reset filter
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
              {/* Native Mobile App Card List (Shown on mobile devices) */}
              <div className="holiday-mobile-cards">
                {filteredItems.map((holiday) => {
                  const dateMeta = getHolidayDateMeta(holiday.date);
                  const isOptional = Boolean(holiday.is_optional || holiday.type === "optional");
                  return (
                    <article key={holiday._id} className="mobile-holiday-card">
                      <div className="mobile-holiday-card-body">
                        {/* App-style Calendar Date Badge */}
                        <div className={`mobile-holiday-date-tile ${isOptional ? "is-optional" : holiday.type === "company" ? "is-company" : "is-public"}`}>
                          <span className="mobile-holiday-month">{dateMeta.month}</span>
                          <span className="mobile-holiday-day">{dateMeta.day}</span>
                          <span className="mobile-holiday-weekday">{dateMeta.weekday}</span>
                        </div>

                        {/* Holiday Info */}
                        <div className="mobile-holiday-info">
                          <div className="mobile-holiday-title-row">
                            <h4 className="mobile-holiday-name">{holiday.name}</h4>
                            <span className={`fiori-status-pill ${holiday.type === "company" ? "is-company" : holiday.type === "optional" ? "is-optional" : "is-public"}`}>
                              {holiday.type || "company"}
                            </span>
                          </div>

                          {holiday.description ? (
                            <p className="mobile-holiday-desc">{holiday.description}</p>
                          ) : null}

                          <div className="mobile-holiday-meta-tags">
                            <span className="mobile-holiday-year-tag">{dateMeta.year}</span>
                            {holiday.region ? (
                              <span className="holiday-region-tag">
                                <MapPin size={12} />
                                <span>{holiday.region}</span>
                              </span>
                            ) : null}
                            {isOptional && (
                              <span className="holiday-optional-tag">Optional leave</span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Touch-Friendly Action Buttons */}
                      <div className="mobile-holiday-actions">
                        <button
                          type="button"
                          className="mobile-holiday-btn edit"
                          onClick={() => editRow(holiday)}
                          title="Edit holiday"
                        >
                          <Pencil size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          className="mobile-holiday-btn delete"
                          onClick={() => del(holiday._id)}
                          title="Delete holiday"
                        >
                          <Trash2 size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>

              {/* Desktop Table View (Shown on larger screens) */}
              <div className="fiori-table-shell holiday-desktop-table">
                <table className="fiori-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Region</th>
                      <th>Optional</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((holiday) => (
                      <tr key={holiday._id}>
                        <td>
                          <strong>{holiday.date}</strong>
                        </td>
                        <td>
                          <div className="fiori-primary-cell">
                            <strong>{holiday.name}</strong>
                            {holiday.description ? <span>{holiday.description}</span> : null}
                          </div>
                        </td>
                        <td>
                          <span className={`fiori-status-pill ${holiday.type === "company" ? "is-company" : holiday.type === "optional" ? "is-optional" : "is-public"}`}>
                            {holiday.type}
                          </span>
                        </td>
                        <td>
                          {holiday.region ? (
                            <span className="holiday-region-cell">
                              <MapPin size={14} />
                              <span>{holiday.region}</span>
                            </span>
                          ) : (
                            <span style={{ color: "#94a3b8" }}>Not set</span>
                          )}
                        </td>
                        <td>{holiday.is_optional ? "Yes" : "No"}</td>
                        <td>
                          <div className="holiday-table-actions">
                            <button
                              type="button"
                              className="fiori-button secondary compact"
                              onClick={() => editRow(holiday)}
                            >
                              <Pencil size={13} /> Edit
                            </button>
                            <button
                              type="button"
                              className="fiori-button secondary danger compact"
                              onClick={() => del(holiday._id)}
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </section>

      {message && (
        <div
          className={`admin-toast ${
            message.toLowerCase().includes("failed") || message.toLowerCase().includes("required")
              ? "is-error"
              : "is-success"
          }`}
        >
          {message}
        </div>
      )}
    </section>
  );
};

export default AdminHolidays;
